import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "../../contexts/AuthContext";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { useToast } from "../../hooks/use-toast";
import type { UserRole } from "../../contexts/AuthContext";

const Register = () => {
  const navigate = useNavigate();
  const { signUp } = useAuth();
  const { toast } = useToast();
  const [isLoading, setIsLoading] = useState(false);
  const [formData, setFormData] = useState({
    email: "",
    password: "",
    confirmPassword: "",
    fullName: "",
    role: "customer" as UserRole,
  });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (formData.password !== formData.confirmPassword) {
      toast({
        title: "Error",
        description: "Passwords do not match",
        variant: "destructive",
      });
      return;
    }

    if (formData.password.length < 6) {
      toast({
        title: "Error",
        description: "Password must be at least 6 characters long",
        variant: "destructive",
      });
      return;
    }

    setIsLoading(true);
    try {
      const { error } = await signUp(formData.email, formData.password, formData.fullName, formData.role);
      if (error) throw error;
      
      toast({
        title: "Success",
        description: "Registration successful! Please check your email to confirm your account.",
      });
      
      navigate('/login');
    } catch (error: any) {
      console.error("Registration error:", error);
      toast({
        title: "Error",
        description: error.message || "Failed to register",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#E8E3DD]">
      <div className="container mx-auto px-4 pt-16 pb-8">
        <div className="max-w-md mx-auto bg-white/95 p-8 rounded-xl shadow-lg">
          <div className="text-center mb-8">
            <h1 className="text-2xl font-bold mb-2 text-[#4A4F41]">Create an Account</h1>
            <p className="text-[#4A4F41]/70">Sign up for MarketBloom.</p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label htmlFor="fullName" className="block text-sm font-medium text-[#4A4F41]/80 mb-1">
                Full Name
              </label>
              <Input
                id="fullName"
                type="text"
                placeholder="Enter your full name"
                value={formData.fullName}
                onChange={(e) => setFormData({ ...formData, fullName: e.target.value })}
                required
                disabled={isLoading}
                className="bg-white border-[#4A4F41]/10 focus:border-[#4A4F41]/30"
              />
            </div>

            <div>
              <label htmlFor="email" className="block text-sm font-medium text-[#4A4F41]/80 mb-1">
                Email
              </label>
              <Input
                id="email"
                type="email"
                placeholder="Enter your email"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                required
                disabled={isLoading}
                className="bg-white border-[#4A4F41]/10 focus:border-[#4A4F41]/30"
              />
            </div>

            <div>
              <label htmlFor="password" className="block text-sm font-medium text-[#4A4F41]/80 mb-1">
                Password
              </label>
              <Input
                id="password"
                type="password"
                placeholder="Create a password (min. 6 characters)"
                value={formData.password}
                onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                required
                disabled={isLoading}
                className="bg-white border-[#4A4F41]/10 focus:border-[#4A4F41]/30"
              />
            </div>

            <div>
              <label htmlFor="confirmPassword" className="block text-sm font-medium text-[#4A4F41]/80 mb-1">
                Confirm Password
              </label>
              <Input
                id="confirmPassword"
                type="password"
                placeholder="Confirm your password"
                value={formData.confirmPassword}
                onChange={(e) => setFormData({ ...formData, confirmPassword: e.target.value })}
                required
                disabled={isLoading}
                className="bg-white border-[#4A4F41]/10 focus:border-[#4A4F41]/30"
              />
            </div>

            <div>
              <label htmlFor="role" className="block text-sm font-medium text-[#4A4F41]/80 mb-1">
                I want to
              </label>
              <select
                id="role"
                className="w-full p-2 border rounded-md bg-white border-[#4A4F41]/10 focus:border-[#4A4F41]/30"
                value={formData.role}
                onChange={(e) => setFormData({ ...formData, role: e.target.value as UserRole })}
                disabled={isLoading}
              >
                <option value="customer">Buy flowers (Customer)</option>
                <option value="florist">Sell flowers (Florist)</option>
              </select>
            </div>

            <Button 
              type="submit" 
              className="w-full bg-[#4A4F41] text-white hover:bg-[#4A4F41]/90" 
              disabled={isLoading}
            >
              {isLoading ? "Creating Account..." : "Create Account"}
            </Button>
          </form>

          <p className="text-center mt-6 text-sm text-[#4A4F41]/70">
            Already have an account?{" "}
            <Link to="/auth" className="text-[#4A4F41] hover:text-[#4A4F41]/80 font-medium">
              Sign in
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
};

export default Register; 